import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Button from '../../../../library/button/button';
import Tooltip from '../../../../library/tooltip/tooltip';
import Typography from '../../../../library/typography/typography';
import GroupAffiliationPromotion from '../../../../components/group-affiliation-promotion/group-affiliation-promotion';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductFlags from '../../../../components/product-flags/product-flags';
import ProductImage from '../../../../components/product-image/product-image';
import ProductPromosOrDTCC from '../../../../components/product-promos-or-dtcc/product-promos-or-dtcc';
import ProductTitle from '../../../../components/product-title/product-title';
import ProductListCardPrice from '../../../product-list-page/components/product-list-card-price/product-list-card-price.one-dt';
import ScrewdriverWrenchRegularIcon from '../../../../shared/icons/screwdriver-wrench-regular-icon';
import EasyPayOptions from '../easy-pay-options/easy-pay-options';

import {
  useAemContent,
  useProductEntryDataLayerValues,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getStaggeredPdpUrl } from '../../../../shared/utils/cart/cart';
import { getProductImpressions } from '../../../../shared/utils/product/product';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import {
  BundleType,
  ProductData,
  PromotionData
} from '../../../../shared/types/graphql-types';
import { MapPrice } from '../../../../shared/types/product';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../../../shared/constants/cms';
import { PageTypes } from '../../../../shared/constants/page-types';

import './pdp-deals-section.scss';

interface TopRecommendedProductData {
  frontProduct?: ProductData;
  rearProduct?: ProductData;
}

interface PdpDealsSectionProps {
  brand?: string;
  combinedMapPrice: MapPrice;
  isAddToCartDisabled: boolean;
  isCanonicalProduct: boolean;
  productType?: string;
  promos: Array<PromotionData>;
  topRecommendedProductData?: TopRecommendedProductData;
}

function PdpDealsSection(props: PdpDealsSectionProps) {
  const {
    brand,
    combinedMapPrice,
    isAddToCartDisabled,
    isCanonicalProduct,
    productType,
    promos,
    topRecommendedProductData
  } = props;

  const prevProductRef = useRef<ProductData | null>(null);
  const { pathname } = useLocation();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { aemTooltipsContent } = useAemContent(AEM_CONTENT_PAGE_IDS.global);
  const { quantity, rearQuantity } = useProductQuantity({
    bundleType: topRecommendedProductData?.rearProduct
      ? BundleType.StaggeredSet
      : undefined
  });

  const inStockTooltip =
    aemTooltipsContent?.[AEM_CONTENT_IDS.inStockNearbyStore]?.tipText;
  const hasPromos = isArrayWithLength(promos);

  const { frontProduct, rearProduct } = topRecommendedProductData || {};
  const firstProduct = frontProduct || rearProduct;

  const topRecommendedProductPdp =
    frontProduct && rearProduct
      ? getStaggeredPdpUrl(frontProduct, rearProduct)
      : firstProduct?.url;

  const productsToTrack = useMemo(() => {
    if (!topRecommendedProductData) return null;

    if (frontProduct && rearProduct) {
      return {
        ...frontProduct,
        rearProduct
      };
    }

    return firstProduct || null;
  }, [frontProduct, rearProduct, firstProduct, topRecommendedProductData]);

  function handleRecommendedProductClick() {
    trackAction(
      'product_tw_oos_recommended_product_view_details_click',
      getProductEntryDataLayerValues(productsToTrack)
    );
  }

  useEffect(() => {
    if (productsToTrack && prevProductRef.current !== productsToTrack) {
      trackAction(
        'product_tw_oos_recommended_product_view',
        getProductImpressions(
          [productsToTrack].map(getProductEntryDataLayerValues),
          pathname
        )
      );
      prevProductRef.current = productsToTrack;
    }
  }, [productsToTrack, getProductEntryDataLayerValues, pathname]);

  return (
    <div styleName="container">
      <GroupAffiliationPromotion showTitle styleName="group-affiliation" />
      {topRecommendedProductData && (
        <div styleName="top-recommended-product-card">
          <div styleName="top-recommended-product-card-banner">
            <ScrewdriverWrenchRegularIcon styleName="screwdriver-wrench-icon" />
            Shop <strong>in-stock tires</strong> for{' '}
            <strong>same-day install</strong>
            <Tooltip content={inStockTooltip} styleName="tooltip" />
          </div>

          <div styleName="top-recommended-product-content">
            <ProductFlags product={firstProduct} styleName="product-flags" />

            <div styleName="top-recommended-product-card-body">
              <ProductImage
                linkUrl={topRecommendedProductPdp}
                onClick={handleRecommendedProductClick}
                product={firstProduct}
                styleName="product-image"
              />

              <div styleName="product-info">
                <ProductTitle
                  brand={firstProduct?.brand}
                  link={topRecommendedProductPdp}
                  name={firstProduct?.name}
                  onClick={handleRecommendedProductClick}
                  styleName="product-title"
                />

                <div styleName="product-info-content">
                  {frontProduct && rearProduct ? (
                    <>
                      <div styleName="product-info-section">
                        <div styleName="product-price-and-size">
                          <Typography tag="span">
                            <Typography tag="span" weight="heavy">
                              FRONT:
                            </Typography>{' '}
                            <Typography styleName="front-size" tag="span">
                              {frontProduct?.size}
                            </Typography>
                          </Typography>
                          <ProductListCardPrice
                            hideFoundItLower
                            hidePercentageSaved
                            product={frontProduct}
                            quantity={quantity}
                            styleName="product-price"
                          />
                        </div>
                        <ProductAvailability
                          product={frontProduct}
                          quantity={quantity}
                          rearProduct={rearProduct}
                          rearQuantity={rearQuantity}
                          styleName="product-availability"
                        />
                      </div>

                      <div styleName="product-info-section">
                        <div styleName="product-price-and-size">
                          <Typography tag="span">
                            <Typography tag="span" weight="heavy">
                              REAR:
                            </Typography>{' '}
                            <Typography styleName="front-size" tag="span">
                              {rearProduct?.size}
                            </Typography>
                          </Typography>
                          <ProductListCardPrice
                            hideFoundItLower
                            hidePercentageSaved
                            product={rearProduct}
                            quantity={quantity}
                            styleName="product-price"
                          />
                        </div>
                        <ProductAvailability
                          product={frontProduct}
                          quantity={quantity}
                          rearProduct={rearProduct}
                          rearQuantity={rearQuantity}
                          styleName="product-availability"
                        />
                      </div>
                    </>
                  ) : (
                    <div styleName="product-info-section">
                      <ProductListCardPrice
                        hideFoundItLower
                        hidePercentageSaved
                        product={firstProduct}
                        quantity={quantity}
                        styleName="product-price"
                      />
                      <ProductAvailability
                        product={firstProduct}
                        quantity={quantity}
                        styleName="product-availability"
                      />
                    </div>
                  )}

                  <Button
                    href={topRecommendedProductPdp || ''}
                    onClick={handleRecommendedProductClick}
                    styleName="product-details-link"
                  >
                    VIEW DETAILS
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <EasyPayOptions />
      <div styleName="deals-promos">
        <ProductPromosOrDTCC
          brand={brand}
          combinedMapPrice={combinedMapPrice}
          hasPromotions={hasPromos}
          pageType={PageTypes.PDP}
          productType={productType}
          promotionData={promos}
          showDtccBanner={
            !isCanonicalProduct && !isAddToCartDisabled && !hasPromos
          }
          showDuration
          showLongDescription
        />
      </div>
    </div>
  );
}

export default PdpDealsSection;
